/**
 * CheckoutPage - Payment checkout with consent confirmation
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/shared/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [consents, setConsents] = useState({
    therapy: false,
    legal: false,
    digital: false
  });
  const level = searchParams.get('level');

  useEffect(() => {
    if (!level) {
      navigate('/');
    }
  }, [level, navigate]);

  const allConsentsChecked = consents.therapy && consents.legal && consents.digital;

  const handleCheckout = async () => {
    if (!allConsentsChecked) {
      toast.error('Please accept all consent statements');
      return;
    }

    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/checkout/session`, {
        level: level,
        origin_url: originUrl
      });

      sessionStorage.setItem('relationship_id', response.data.relationship_id);
      
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      const detail = error?.response?.data?.detail;
      toast.error(detail ? `Checkout failed: ${detail}` : 'Failed to create checkout session');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header showBack={true} />
      <div className="flex items-center justify-center p-6" style={{minHeight: 'calc(100vh - 100px)'}}>
        <div className="max-w-lg w-full bg-white rounded-2xl border border-stone-100 shadow-sm p-12">
          <h2 className="text-3xl font-bold text-stone-900 mb-6" data-testid="checkout-heading">
            Confirm Your MarrsingClause® Level
          </h2>
          
          <p className="text-stone-600 leading-relaxed mb-8">
            This experience includes guided reflections, partner collaboration, and a personalized MarrsingClause® Certificate.
          </p>

          <div className="bg-stone-50 rounded-xl p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm uppercase tracking-widest text-stone-500">Selected Level</span>
              <span className="text-2xl font-bold text-stone-900">{level}</span>
            </div>
          </div>

          <div className="space-y-4 mb-8">
            <label className="flex items-start gap-3 cursor-pointer">
              <input 
                type="checkbox" 
                className="mt-1" 
                checked={consents.therapy}
                onChange={(e) => setConsents({...consents, therapy: e.target.checked})}
                data-testid="consent-not-therapy" 
              />
              <span className="text-sm text-stone-600">This is not therapy or counseling</span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input 
                type="checkbox" 
                className="mt-1" 
                checked={consents.legal}
                onChange={(e) => setConsents({...consents, legal: e.target.checked})}
                data-testid="consent-not-legal" 
              />
              <span className="text-sm text-stone-600">This is not legal advice or a legal contract</span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input 
                type="checkbox" 
                className="mt-1" 
                checked={consents.digital}
                onChange={(e) => setConsents({...consents, digital: e.target.checked})}
                data-testid="consent-digital" 
              />
              <span className="text-sm text-stone-600">I consent to digital delivery and records</span>
            </label>
          </div>

          <button
            onClick={handleCheckout}
            disabled={loading || !allConsentsChecked}
            className="w-full bg-mc-blue-deep text-white hover:bg-mc-blue-dark rounded-full px-8 py-4 text-lg font-medium transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="proceed-to-payment"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Processing...
              </>
            ) : (
              'Proceed Securely'
            )}
          </button>
          <p className="text-xs text-center text-stone-400 mt-3">Secure checkout. Immediate access after purchase.</p>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
